import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UnpublishedListingsByDaysResponse } from 'dashboard/components/visuals-dashboard/widgets/unpublished-listings-by-days-widget/unpublished-listings-by-days-data-response.interface';
import { IceDashboardDisplayStateEnum } from 'dashboard/enums/visuals/ice-dashboard-display-state.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import {
    IceVisualsDashboardTableDisplayComponent,
    TableColumn,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-table-display/ice-visuals-dashboard-table-display.component';

interface ListingData {
    id: string;
    name: string;
    days: string;
}

@Component({
    selector: 'app-unpublished-listings-by-days-widget',
    imports: [TranslateModule, IceVisualsDashboardTableDisplayComponent],
    templateUrl: './unpublished-listings-by-days-widget.component.html',
    styleUrl: './unpublished-listings-by-days-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class UnpublishedListingsByDaysWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    iceDashboardDisplayStateEnum = IceDashboardDisplayStateEnum;

    onLangChange = toSignal(this.translateService.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    columns: TableColumn[] = [
        { key: 'id', translationKey: 'dashboard.visuals.widgets.unpublishedListingsByDays.table.columns.id', width: '30%' },
        {
            key: 'days',
            translationKey: 'dashboard.visuals.widgets.unpublishedListingsByDays.table.columns.days',
            width: '40%',
        },
        { key: 'name', translationKey: 'dashboard.visuals.widgets.unpublishedListingsByDays.table.columns.name', width: '40%' },
    ];

    totalListings = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return 0;
        }
        return widgetData.totalListings;
    });

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    listings = computed<ListingData[]>(() => {
        const widgetData = this.dataService.data();
        this.onLangChange();
        if (!widgetData || !widgetData.ranges) {
            return [];
        }
        return widgetData.ranges.map((range) => ({
            id: range.mappedId,
            name: range.title,
            days: this.formatDaysLabel(range.days),
        }));
    });

    widgetState = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return null;
        }
        return widgetData.totalListings === 0 ? IceDashboardDisplayStateEnum.SUCCESS : IceDashboardDisplayStateEnum.WARNING;
    });

    constructor(
        public dataService: VisualsDashboardDataService<UnpublishedListingsByDaysResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
        effect(() => {
            const state = this.widgetState();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && state !== null) {
                this.visualsDashboardService.setHtmlIdBackgroundStateOverride(widgetHtmlId, state);
            }
        });
    }

    private formatDaysLabel(days: number): string {
        if (days < 1) {
            return (
                '< ' +
                this.translateService.instant('dashboard.visuals.widgets.unpublishedListings.dayLabels.singular', { numberOfDays: 1 })
            );
        }
        const translationKey =
            days === 1
                ? 'dashboard.visuals.widgets.unpublishedListings.dayLabels.singular'
                : 'dashboard.visuals.widgets.unpublishedListings.dayLabels.plural';
        return this.translateService.instant(translationKey, { numberOfDays: days });
    }
}
